/**
 * Almacenamiento de sesion compartido entre masshappy.com y sus subdominios.
 *
 * En el dominio de produccion la sesion se guarda en cookies del dominio padre
 * (.masshappy.com) para que masshappy.com y app.masshappy.com compartan login.
 * En cualquier otro host (previews de lovable.app, localhost, etc.) se delega
 * en localStorage, porque el navegador rechazaria una cookie de otro dominio.
 */

const COOKIE_DOMAIN = ".masshappy.com";
const MAX_CHUNK_BYTES = 3000;
const ONE_YEAR = 60 * 60 * 24 * 365;

type SimpleStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const noopStorage: SimpleStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const isMasshappyHost = (): boolean => {
  const host = window.location.hostname;
  return host === "masshappy.com" || host.endsWith(".masshappy.com");
};

const readCookie = (name: string): string | null => {
  const prefix = `${encodeURIComponent(name)}=`;
  const found = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
};

const writeCookie = (name: string, value: string) => {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; domain=${COOKIE_DOMAIN}; SameSite=Lax; Secure; max-age=${ONE_YEAR}`;
};

const deleteCookie = (name: string) => {
  document.cookie = `${encodeURIComponent(name)}=; path=/; domain=${COOKIE_DOMAIN}; SameSite=Lax; Secure; max-age=0`;
};

const cookieBackend: SimpleStorage = {
  getItem: (key) => {
    const direct = readCookie(key);
    if (direct !== null) return direct;

    let result = "";
    let index = 0;
    for (;;) {
      const chunk = readCookie(`${key}.${index}`);
      if (chunk === null) break;
      result += chunk;
      index += 1;
    }
    return index > 0 ? result : null;
  },
  setItem: (key, value) => {
    // Se limpia primero para no dejar restos de un reparto anterior
    cookieBackend.removeItem(key);

    if (value.length <= MAX_CHUNK_BYTES) {
      writeCookie(key, value);
      return;
    }

    let index = 0;
    for (let start = 0; start < value.length; start += MAX_CHUNK_BYTES) {
      writeCookie(`${key}.${index}`, value.slice(start, start + MAX_CHUNK_BYTES));
      index += 1;
    }
  },
  removeItem: (key) => {
    deleteCookie(key);
    let index = 0;
    while (readCookie(`${key}.${index}`) !== null) {
      deleteCookie(`${key}.${index}`);
      index += 1;
    }
  },
};

const resolveStorage = (): SimpleStorage => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return noopStorage;
  }
  if (!isMasshappyHost()) {
    return window.localStorage;
  }
  return cookieBackend;
};

const backend = resolveStorage();

export const cookieStorage: SimpleStorage = {
  getItem: (key) => backend.getItem(key),
  setItem: (key, value) => backend.setItem(key, value),
  removeItem: (key) => backend.removeItem(key),
};

export default cookieStorage;
